/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';

/**
 * Create a hook that controls the pagination of an
 * items array. It slices the given array to display
 * the page of interest based on the current page.
 *
 * @param items The full array of items to paginate.
 * @param pageSize The number of items to display
 * in the page.
 */
export const usePagination = <T>(items: T[], pageSize: number = 20) => {
    const [currentPage, setCurrentPage] = React.useState<number>(0);

    const pageCount = React.useMemo(() => Math.ceil(items.length / pageSize) || 1, [items, pageSize]);

    const pageItems = React.useMemo(() => {
        const itemSliceStart = pageSize * currentPage;
        const itemSliceEnd = itemSliceStart + pageSize;

        return items.slice(itemSliceStart, itemSliceEnd);
    }, [pageSize, currentPage, items]);

    /**
     * If all items in the last page were deleted,
     * then the page count decreases, and the current
     * page remains the same. In order to accommodate for
     * that, if the current page is larger than the
     * page count, the current page is decreased by one.
     */
    React.useEffect(() => {
        if (currentPage >= pageCount) {
            setCurrentPage(currentPage - 1);
        }
    }, [currentPage, pageCount]);

    return { currentPage, setCurrentPage, pageItems, pageCount };
};
